/**
 * 🎯 Hook useTechnologies - Gestion des données des technologies
 * 
 * Hook React personnalisé pour gérer les données des technologies depuis l'API locale.
 * Fournit un accès centralisé aux informations sur les technologies avec mise en cache,
 * filtrage et support multilingue.
 * 
 * Fonctionnalités :
 * - 🗄️ Intégration avec l'API PostgreSQL locale pour les données
 * - 🌐 Support bilingue (français/anglais)
 * - 📊 Filtrage par catégorie
 * - ⚡ Performances optimisées avec mise en cache
 * - 🔄 Support des mises à jour en temps réel
 * - 📱 Chargement réactif des données
 * - 🚫 Protection anti-boucle avec cache TTL
 */

import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { apiService } from '../services/apiService';

/**
 * 🏗️ Interface Technology
 * 
 * Définit la structure d'un objet technologie stocké en base de données.
 * Contient toutes les informations nécessaires pour afficher les icônes de technologie
 * avec un support de localisation approprié.
 */
export interface Technology {
  id: string;
  name: string;
  icon_name: string;
  color: string;
  category: string;
  name_fr?: string;
  name_en?: string;
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
  // URL de l'image/logo hébergé (migration 2025-08-28)
  icon_url?: string | null;
  image_url?: string | null;
  logo_url?: string | null;
}

/**
 * 🎯 Interface de retour du Hook
 * 
 * Définit le type de retour du hook useTechnologies.
 * Fournit des données complètes et des fonctions utilitaires pour
 * gérer les technologies dans les composants.
 */
export interface UseTechnologiesReturn {
  technologies: Technology[];
  loading: boolean;
  error: string | null;
  categories: string[];
  getTechByName: (name: string) => Technology | undefined;
  getTechsByCategory: (category: string) => Technology[];
  getLocalizedName: (tech: Technology) => string;
  refreshTechnologies: () => Promise<void>;
}

/**
 * 🔧 Hook useTechnologies
 * 
 * Hook principal pour gérer les données des technologies.
 * Gère la récupération des données, la mise en cache, le filtrage et la localisation.
 * 
 * @returns UseTechnologiesReturn - Données des technologies et fonctions utilitaires
 */
export const useTechnologies = (): UseTechnologiesReturn => {
  // Gestion de l'état
  const [technologies, setTechnologies] = React.useState<Technology[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  
  // Contexte de langue pour la localisation
  const { language } = useLanguage();
  
  // ✅ PROTECTION - Évite les appels simultanés
  const loadingRef = React.useRef<boolean>(false);

  /**
   * 📡 Récupère les technologies depuis l'API du backend
   * 
   * Récupère toutes les technologies actives depuis l'API,
   * triées par catégorie et index pour un affichage cohérent.
   * PROTECTION : Utilise apiService avec cache TTL et déduplication.
   */
  const fetchTechnologies = React.useCallback(async (): Promise<void> => {
    // ✅ PROTECTION - Évite les appels simultanés
    if (loadingRef.current) {
      console.log('🔒 useTechnologies : Appel déjà en cours, ignoré');
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      // ✅ PROTECTION ANTI-BOUCLE - Utilise apiService avec cache de 3 minutes
      const response = await apiService.get<Technology[]>('/technologies', true, {
        cacheTtlMs: 180000, // Cache de 3 minutes pour les technologies
        useCache: true
      });

      if (response && Array.isArray(response)) {
        setTechnologies(response);
      } else {
        setTechnologies([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue lors du chargement des technologies';
      setError(errorMessage);
      console.error('Erreur lors du chargement des technologies :', err);
      // En cas d'erreur, on garde les données existantes si disponibles
      if (technologies.length === 0) {
        setTechnologies([]);
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []); // ✅ CORRECTION - Dépendances vides pour éviter les boucles infinies

  /**
   * 🔄 Rafraîchir les technologies
   * 
   * Fonction publique pour rafraîchir manuellement les données des technologies.
   * Utile pour les mises à jour en temps réel ou la récupération après erreur.
   */
  const refreshTechnologies = React.useCallback(async (): Promise<void> => {
    await fetchTechnologies();
  }, [fetchTechnologies]);

  // Chargement initial des données
  React.useEffect(() => {
    fetchTechnologies();
  }, []); // ✅ CORRECTION MAJEURE - Dépendances vides pour charger une seule fois

  /**
   * 📊 Récupère les catégories uniques
   * 
   * Extrait les catégories uniques des technologies pour le filtrage.
   * Mémoïsé pour l'optimisation des performances.
   */
  const categories = React.useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(technologies.map((tech: Technology) => tech.category))
    );
    return uniqueCategories.sort();
  }, [technologies]);

  /**
   * 🔍 Récupère une technologie par son nom
   * 
   * Trouve une technologie par son nom (insensible à la casse).
   * Supporte les correspondances exactes et les recherches normalisées.
   * 
   * @param name - Nom de la technologie à rechercher
   * @returns Objet Technology ou undefined si non trouvé
   */
  const getTechByName = (name: string): Technology | undefined => {
    // Normalisation des accents/espaces/ponctuation pour des correspondances flexibles
    const normalize = (v?: string) => (v || '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .replace(/[^a-z0-9]+/g, ' ')       // Remplace les caractères non alphanumériques par un espace
      .replace(/\s+/g, ' ')             // Supprime les espaces multiples
      .trim();

    const slugify = (v?: string) => normalize(v).replace(/\s+/g, ''); // Supprime les espaces

    const searchNorm = normalize(name);
    const searchSlug = slugify(name);

    return technologies.find((tech: Technology) => {
      const nName = normalize(tech.name);
      const nIcon = normalize(tech.icon_name);
      const nFr = normalize(tech.name_fr);
      const nEn = normalize(tech.name_en);

      const sName = slugify(tech.name);
      const sIcon = slugify(tech.icon_name);
      const sFr = slugify(tech.name_fr);
      const sEn = slugify(tech.name_en);

      return (
        // Égalité stricte normalisée
        nName === searchNorm || nIcon === searchNorm || nFr === searchNorm || nEn === searchNorm ||
        // Égalité slug (ex: 'vanillaextract' == 'vanilla-extract')
        sName === searchSlug || sIcon === searchSlug || sFr === searchSlug || sEn === searchSlug
      );
    });
  };

  /**
   * 📋 Récupère les technologies par catégorie
   * 
   * Filtre les technologies par catégorie.
   * Retourne les technologies triées par order_index et par nom.
   * 
   * @param category - Catégorie à filtrer
   * @returns Tableau de technologies dans la catégorie spécifiée
   */
  const getTechsByCategory = (category: string): Technology[] => {
    return technologies
      .filter((tech: Technology) => tech.category.toLowerCase() === category.toLowerCase())
      .sort((a: Technology, b: Technology) => {
        // Tri par order_index en premier, puis par nom
        if (a.order_index !== b.order_index) {
          return a.order_index - b.order_index;
        }
        return a.name.localeCompare(b.name);
      });
  };

  /**
   * 🌐 Récupère le nom localisé
   * 
   * Retourne le nom approprié en fonction de la langue actuelle.
   * Utilise le nom de base si la version localisée n'est pas disponible.
   * 
   * @param tech - Objet technologie
   * @returns Chaîne de caractères du nom localisé
   */
  const getLocalizedName = (tech: Technology): string => {
    if (language === 'fr' && tech.name_fr) {
      return tech.name_fr;
    }
    if (language === 'en' && tech.name_en) {
      return tech.name_en;
    }
    return tech.name;
  };

  // Retourne l'interface du hook
  return {
    technologies,
    loading,
    error,
    categories,
    getTechByName,
    getTechsByCategory,
    getLocalizedName,
    refreshTechnologies
  };
};

export default useTechnologies;
